import "reflect-metadata";
import { z } from "zod";
import type { ToolInterface } from "./tool";

interface ToolMetadata {
  name?: string;
  description: string;
  parameters: z.ZodTypeAny;
}

const toolMetadataKey = Symbol("toolMetadata");

/**
 * Decorator to mark a method as a tool with a name
 * Optional - if not provided, the method name will be used
 */
export const ToolName =
  (name: string) =>
  (target: any, propertyKey: string, _descriptor: PropertyDescriptor) => {
    const metadata: Partial<ToolMetadata> =
      Reflect.getMetadata(toolMetadataKey, target, propertyKey) || {};

    Reflect.defineMetadata(
      toolMetadataKey,
      { ...metadata, name },
      target,
      propertyKey
    );
  };

/**
 * Decorator to add a description to a tool method
 */
export const ToolDescription =
  (description: string) =>
  (target: any, propertyKey: string, _descriptor: PropertyDescriptor) => {
    const metadata: Partial<ToolMetadata> =
      Reflect.getMetadata(toolMetadataKey, target, propertyKey) || {};

    Reflect.defineMetadata(
      toolMetadataKey,
      { ...metadata, description },
      target,
      propertyKey
    );
  };

/**
 * Decorator to define a parameter for a tool method
 * @param name Parameter name
 * @param description Parameter description
 * @param type Zod schema for parameter validation
 */
export const ToolParam =
  (name: string, description: string, type: z.ZodTypeAny) =>
  (target: any, propertyKey: string, _parameterIndex: number) => {
    const metadata: Partial<ToolMetadata> =
      Reflect.getMetadata(toolMetadataKey, target, propertyKey) || {};

    const parameters = metadata.parameters || z.object({});
    const updatedParameters = z.object({
      ...((parameters as z.ZodObject<any>).shape || {}),
      [name]: type.describe(description),
    });

    Reflect.defineMetadata(
      toolMetadataKey,
      { ...metadata, parameters: updatedParameters },
      target,
      propertyKey
    );
  };

/**
 * Get all tool methods from a class instance
 */
export const getToolMethods = (instance: any): ToolInterface[] => {
  const prototype = Object.getPrototypeOf(instance);
  const propertyNames = Object.getOwnPropertyNames(prototype);

  return propertyNames
    .map((propertyName) => {
      const metadata: ToolMetadata | undefined = Reflect.getMetadata(
        toolMetadataKey,
        prototype,
        propertyName
      );

      if (!metadata || !metadata.description || !metadata.parameters) {
        return null;
      }

      const method = prototype[propertyName];

      return {
        name: metadata.name || propertyName,
        description: metadata.description,
        parameters: metadata.parameters,
        executeTool: method.bind(instance),
      };
    })
    .filter((tool): tool is ToolInterface => tool !== null);
};
